import { defineBlock, EditableText } from "eddev/blocks"
import { cn, tv, VariantProps } from "@utils/tw"
import { ComponentProps, ComponentPropsWithoutRef, forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { Link } from "eddev/routing"
import { ARROW_DOWN, ARROW_RIGHT, CARET_DOWN } from "@components/atoms/Icons"
import { format } from "date-fns"
import { useEvData, useRoadSafetyData, useTransportData } from "@hooks/queries"
import { twMerge } from "tailwind-merge"
import { getNextSiblingPage } from "@utils/getNextSiblingPage"
import { useNextPagePreview } from "@hooks/useNextPagePreview"

export const meta: BlockMeta = {
  title: "Data Dashboard Section Header",
  category: "common",
  icon: "minus",
  tags: ["root", "evindex"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
    templates: ["templates/road-safety", "templates/transport-affordability", "templates/ev-index"],
  },
}

export default defineBlock("data/road-safety-section-header", (props) => {
  const [date, setDate] = useState<Date | string | null>(null)
  const template = props.page?.template?.templateName
  const evIndexData = useEvData(undefined)
  const roadSafetyData = useRoadSafetyData(undefined)
  const transportAffordabilityData = useTransportData(undefined)
  const ref = useRef<HTMLDivElement>(null)

  // When previewed at the bottom of the previous page, link to this block's own page instead
  const isPreview = useNextPagePreview()

  // Next sibling page (by menu order), only if it shares the EV Index template
  const nextEvIndexUri = useMemo(
    () =>
      isPreview
        ? props.page?.uri ?? undefined
        : getNextSiblingPage(props.page?.parent?.node, props.page?.databaseId, "Data: EV Index")?.uri ?? undefined,
    [isPreview, props.page],
  )

  useEffect(() => {
    if (template === "Data: Road Safety") {
      if (roadSafetyData.data?.roadSafetyData) {
        const dataset = roadSafetyData.data.roadSafetyData.map((data) => JSON.parse(data?.data ?? ""))
        const returnedData = [] as Date[]
        dataset.forEach((line: any) => {
          if (!line["Date"]) return
          const [m, y] = line["Date"].split("-")
          const date = new Date(`${m}-01-${y}`)
          returnedData.push(date)
        })
        setDate(
          returnedData.sort((a: Date, b: Date) => {
            return a.getTime() - b.getTime()
          })[returnedData.length - 1],
        )
      }
    }
    if (template === "Data: Transport Affordability") {
      if (transportAffordabilityData.data?.transportAffordabilityData) {
        const dataset = transportAffordabilityData.data.transportAffordabilityData.map((data) =>
          JSON.parse(data?.data ?? ""),
        )

        const sorted = Object.keys(dataset[0])
          .filter((key) => key.startsWith("Q"))
          .sort((a, b) => {
            // Extract year and quarter from strings
            const [, quarterA, yearA] = a.match(/Q(\d+) (\d+)/) ?? []
            const [, quarterB, yearB] = b.match(/Q(\d+) (\d+)/) ?? []
            // Compare years first
            if (yearA !== yearB) {
              return parseInt(yearA) - parseInt(yearB)
            }
            // If years are the same, compare quarters
            return parseInt(quarterA) - parseInt(quarterB)
          })
        setDate(sorted[sorted.length - 1])
      }
    }
    if (template === "Data: EV Index") {
      if (evIndexData.data?.evIndexData) {
        const dataset = evIndexData.data.evIndexData.map((data) => JSON.parse(data?.data ?? ""))

        const sorted = Object.keys(dataset[0])
          .filter((key) => key.startsWith("Q"))
          .sort((a, b) => {
            // Extract year and quarter from strings
            const [, quarterA, yearA] = a.match(/Q(\d+) (\d+)/) ?? []
            const [, quarterB, yearB] = b.match(/Q(\d+) (\d+)/) ?? []
            // Compare years first
            if (yearA !== yearB) {
              return parseInt(yearA) - parseInt(yearB)
            }
            // If years are the same, compare quarters
            return parseInt(quarterA) - parseInt(quarterB)
          })
        setDate(sorted[sorted.length - 1])
      }
    }
  }, [
    template,
    roadSafetyData.data?.roadSafetyData,
    evIndexData.data?.evIndexData,
    transportAffordabilityData.data?.transportAffordabilityData,
  ])

  const [nextSection, setNextSection] = useState<Element>()
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const sections = document.querySelectorAll(".data-next")

      const currentNumber = ref.current?.querySelector("[data-number]")?.innerHTML

      // Make sure its not the last section
      Object.values(sections).forEach((section, i) => {
        // Loop through the sections
        if (section === ref.current) {
          // Check if the next section has the same number
          for (let x = i + 1; x < sections.length; x++) {
            const nextSectionNumber = sections[x].querySelector("[data-number]")?.innerHTML

            if (currentNumber !== nextSectionNumber) {
              setNextSection(Object.values(sections)[x])
              break
            }
          }
        }
      })
    }, 2000)
    return () => clearTimeout(timeoutId)
  }, [])

  const smooth = (e: MouseEvent) => {
    e.preventDefault()
    if (nextSection) {
      window.scrollTo({
        top: window.scrollY + nextSection.getBoundingClientRect().top - 100,
        behavior: "smooth",
      })
    }
  }

  return (
    <div className="w-full border-t border-b border-solid border-grey">
      <div className="grid-auto gap-6 px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px]">
        <div className="col-span-12">
          <div className="data-next w-full flex justify-between" ref={ref}>
            <ContentWrap>
              <Counter>
                <EditableText as={`span`} placeholder="x" store="counter" data-number />
              </Counter>
              <Content>
                <EditableText as="h2" className="!type-title-s" placeholder="Enter title" store="title" />
                {props.overrideDateValue ? (
                  <span>{props.overrideValue}</span>
                ) : (
                  <>
                    {date && template === "Data: Road Safety" && (
                      <span>(as at end {format(date as Date, "MMMM yyyy")})</span>
                    )}
                    {date && template === "Data: Transport Affordability" && (
                      <span>Per household ({date as String})</span>
                    )}
                    {date && template === "Data: EV Index" && <span>Last updated {date as String}</span>}
                  </>
                )}
              </Content>
            </ContentWrap>

            {template === "Data: EV Index" ? (
              nextEvIndexUri && (
                <div className="flex">
                  {!isPreview && (
                    <Link
                      href="/research-data/electric-vehicle/"
                      className={twMerge(
                        "hidden md:flex",
                        "items-center border-l border-l-grey pl-4 md:px-6",
                        "[&_span]:type-label-caps [&_span]:font-bold [&_span]:!text-[12px] [&_span]:pr-3",
                        "[&_svg]:text-yellow",
                      )}
                    >
                      <span>Index</span>
                      {CARET_DOWN}
                    </Link>
                  )}
                  <Link
                    href={nextEvIndexUri}
                    className={twMerge(
                      isPreview ? "flex" : "hidden md:flex",
                      "items-center border-l border-l-grey pl-4 md:px-6",
                      "[&_span]:type-label-caps [&_span]:font-bold [&_span]:!text-[12px] [&_span]:pr-3",
                      "[&_svg]:text-yellow",
                      "xl:pr-0",
                    )}
                  >
                    <span>{isPreview ? "Explore" : "Next"}</span>
                    {ARROW_RIGHT}
                  </Link>
                </div>
              )
            ) : (
              <SLink
                href=""
                className="next-link"
                hide={nextSection === undefined}
                onClick={(e: MouseEvent) => smooth(e)}
              >
                <span>Next</span>
                {ARROW_DOWN}
              </SLink>
            )}
          </div>
        </div>
      </div>
    </div>
  )
})

const ContentWrap = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex py-5", className)} {...props} />
))
ContentWrap.displayName = "ContentWrap"

const Counter = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex w-[40px] h-[40px] min-w-[40px] min-h-[40px] rounded-[40px] bg-black mr-5 items-center justify-center",
      "[&_span]:type-title-s [&_span]:text-white",
      className,
    )}
    {...props}
  />
))
Counter.displayName = "Counter"

const Content = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col", "[&_span]:type-body-xs [&_span]:text-mid-grey", className)}
    {...props}
  />
))
Content.displayName = "Content"

const sLinkStyles = tv({
  base: [
    "hidden items-center border-l border-l-grey px-9",
    "[&_span]:type-label-caps [&_span]:font-bold [&_span]:!text-[12px] [&_span]:pr-3",
    "[&_svg]:text-yellow",
    "md:flex",
    "xl:pr-0",
  ],
  variants: {
    hide: {
      true: "opacity-0 pointer-events-none",
    },
  },
})

const SLink = forwardRef<HTMLAnchorElement, ComponentProps<typeof Link> & VariantProps<typeof sLinkStyles>>(
  ({ hide, className, ...props }, ref) => (
    <Link ref={ref} className={cn(sLinkStyles({ hide }), className)} {...props} />
  ),
)
SLink.displayName = "SLink"
